import { InfiniteData, QueryKey, UseInfiniteQueryOptions, UseMutationOptions, UseQueryOptions, keepPreviousData } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { deserialize } from 'serializr';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { QueryKeys } from '../../enums/queryKeys';
import { ClientsListResponse } from '../../models/clientManager/clientList.model';
import { ClientDetails } from '../../models/clientManager/clientDetails.model';
import type {
  AddOnCatalogueItem,
  AddOnsResponse,
  ArchiveClientResponse,
  ClientGroup,
  ClientGroupsPage,
  ClientListItem,
  ClientPayload,
  ClientRecord,
  ClientUpdatePayload,
  CreateClientResponse,
  DeleteClientResponse,
  PaymentGroup,
  PaymentGroupsResponse,
  PaymentOrganizationModel,
  SubscriptionType,
  SubscriptionTypesResponse,
  UpdateClientResponse,
} from '../../types/ClientManagementTypes';

const GROUPS_PER_PAGE = 5;

// Story 1 wants every read to return active + archived, so neither flag ever
// varies -- they are constants rather than arguments, and so key no cache.
const SHOW_ARCHIVED = true;
const SHOW_DELETED = false;

// Both lists change rarely and every wizard reads the same ones.
const CATALOGUE_STALE_TIME_MS = 5 * 60 * 1000;

// Long enough that expanding a row and then editing that client reuses one fetch,
// short enough that a stale record never reaches the wizard's form.
const CLIENT_DETAILS_STALE_TIME_MS = 30 * 1000;

interface ClientApiErrorBody {
  error?: string;
  errorCode?: string;
  details?: { field: string; message: string }[] | Record<string, string>;
}

/**
 * Not `shared/utils/apiErrorMessage.ts`: that reads `{message}` or
 * `{error:{code,message}}`, while this API sends a flat `error` plus a `details`
 * (array on VALIDATION_ERROR, or object on 500 errors).
 */
export const getClientManagerErrorMessage = (error: unknown): string | undefined => {
  const { response, message } = error as AxiosError<ClientApiErrorBody>;
  const data = response?.data;

  if (Array.isArray(data?.details)) {
    const msgs = data.details.map((d) => d?.message).filter(Boolean);
    if (msgs.length) return msgs.join(' ');
  } else if (typeof data?.details === 'object' && data.details !== null) {
    const msgs = Object.values(data.details).filter((v) => typeof v === 'string');
    if (msgs.length) return msgs.join(' ');
  }
  return data?.error || message;
};

export const buildClientPayload = (clientId: number, payload: ClientUpdatePayload) => ({ id: clientId, payload });

const toClientRecord = (client: ClientListItem): ClientRecord => ({
  id: client.id,
  name: client.clientName,
  adminEmail: client.adminEmail,
  subscriptionTier: client.subscriptionType,
  expiration: client.subscriptionExpiresAt,
  storageUsedGb: client.storageUsedGB,
  storageTotalGb: client.storageTotalGB,
  cost: client.cost,
  status: client.status,
  archived: client.archived,
  hasAddons: client.hasAddons ?? false,
});

const toClientGroup = (organization: PaymentOrganizationModel): ClientGroup => ({
  id: organization.paymentGroupId,
  name: organization.paymentGroupName,
  clients: organization.clients.filter((client) => !client.archived).map(toClientRecord),
});

const ClientManagerService = () => {
  const getClientGroupsInfinite = (search: string): UseInfiniteQueryOptions<ClientGroupsPage, Error, InfiniteData<ClientGroupsPage>, QueryKey, number> => ({
    queryKey: [QueryKeys.GET_CLIENT_GROUPS, search],
    queryFn: async ({ pageParam }) => {
      const { data } = await axiosInstance.get(ApiRoutes.CLIENTS, {
        params: { ...(search ? { search } : {}), limit: GROUPS_PER_PAGE, offset: pageParam, showArchived: SHOW_ARCHIVED, showDeleted: SHOW_DELETED },
      });
      const { paymentOrganizations, pagination } = deserialize(ClientsListResponse, data);

      return {
        groups: paymentOrganizations.map(toClientGroup),
        // Emitted loose rather than as a group: pages arrive one at a time and
        // the Archive group is a single group across all of them.
        archivedClients: paymentOrganizations.flatMap((organization) => organization.clients.filter((client) => client.archived).map(toClientRecord)),
        nextOffset: pagination.hasMore ? pagination.offset + pagination.limit : null,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    // Story 1: the listing must show current data on every open and refresh.
    staleTime: 0,
    // The search is debounced, so a new key resolves mid-typing; without this the
    // table blanks between keystrokes.
    placeholderData: keepPreviousData,
  });

  const getClientDetails = (clientId: number): UseQueryOptions<ClientDetails, Error> => ({
    queryKey: [QueryKeys.GET_CLIENT_DETAILS, clientId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(ApiRoutes.CLIENT_BY_ID.replace(':id', String(clientId)));
      return deserialize(ClientDetails, data);
    },
    staleTime: CLIENT_DETAILS_STALE_TIME_MS,
  });

  const createClient = (): UseMutationOptions<CreateClientResponse, Error, ClientPayload> => ({
    mutationFn: async (payload) => {
      const { data } = await axiosInstance.post(ApiRoutes.CLIENTS, payload);
      return data;
    },
  });

  const updateClient = (): UseMutationOptions<UpdateClientResponse, Error, ReturnType<typeof buildClientPayload>> => ({
    mutationFn: async ({ id, payload }) => {
      const { data } = await axiosInstance.put(ApiRoutes.CLIENT_BY_ID.replace(':id', String(id)), payload);
      return data;
    },
  });

  const archiveClient = (): UseMutationOptions<ArchiveClientResponse, Error, number> => ({
    mutationFn: async (clientId) => {
      const { data } = await axiosInstance.patch(ApiRoutes.CLIENT_ARCHIVE.replace(':id', String(clientId)), { archived: true });
      return data;
    },
  });

  const unarchiveClient = (): UseMutationOptions<ArchiveClientResponse, Error, number> => ({
    mutationFn: async (clientId) => {
      const { data } = await axiosInstance.patch(ApiRoutes.CLIENT_UNARCHIVE.replace(':id', String(clientId)), { archived: false });
      return data;
    },
  });

  const deleteClient = (): UseMutationOptions<DeleteClientResponse, Error, number> => ({
    mutationFn: async (clientId) => {
      const { data } = await axiosInstance.delete(ApiRoutes.CLIENT_BY_ID.replace(':id', String(clientId)));
      return data;
    },
  });

  const getAddOns = (): UseQueryOptions<AddOnCatalogueItem[], Error> => ({
    queryKey: [QueryKeys.GET_ADD_ONS],
    queryFn: async () => {
      const { data } = await axiosInstance.get<AddOnsResponse>(ApiRoutes.ADD_ONS);
      return data.addons;
    },
    staleTime: CATALOGUE_STALE_TIME_MS,
  });

  const getSubscriptionTypes = (): UseQueryOptions<SubscriptionType[], Error> => ({
    queryKey: [QueryKeys.GET_SUBSCRIPTION_TYPES],
    queryFn: async () => {
      const { data } = await axiosInstance.get<SubscriptionTypesResponse>(ApiRoutes.SUBSCRIPTION_TYPES);
      return data.subscriptionTypes;
    },
    staleTime: CATALOGUE_STALE_TIME_MS,
  });

  const getPaymentGroups = (): UseQueryOptions<PaymentGroup[], Error> => ({
    queryKey: [QueryKeys.GET_PAYMENT_GROUPS],
    queryFn: async () => {
      const { data } = await axiosInstance.get<PaymentGroupsResponse>(ApiRoutes.PAYMENT_GROUPS);
      return data.paymentGroups;
    },
    staleTime: CATALOGUE_STALE_TIME_MS,
  });

  const createPaymentGroup = (): UseMutationOptions<PaymentGroup & { message: string }, Error, { name: string }> => ({
    mutationFn: async ({ name }) => {
      const { data } = await axiosInstance.post(ApiRoutes.PAYMENT_GROUPS, { name });
      return data;
    },
  });

  return {
    getClientGroupsInfinite,
    getClientDetails,
    createClient,
    updateClient,
    archiveClient,
    unarchiveClient,
    deleteClient,
    getAddOns,
    getSubscriptionTypes,
    getPaymentGroups,
    createPaymentGroup,
  };
};

export default ClientManagerService;
